// View-model derivation for the open-orders card view. Pure functions, so the
// card layout computes order display identically to the Open Orders table it
// mirrors. Most of that table is still rendered inline in PositionTracker and
// kept in lockstep by hand; the EXPIRY decision no longer is — #517 moved it
// behind the exported `isGtsEquivalentExpiry` / `expiryPrecedesEventStart` /
// GTS_EQUIVALENCE_TOLERANCE_MS, which both surfaces call, so the one comparison
// a trader acts on cannot drift between them.

import { canEditKalshiOrder, type KalshiOrder } from '../../types/kalshi';
import type { PolyRelayOpenOrder } from '../../lib/polymarket/relay';
import { truncateTicker } from '../../lib/kalshiTicker/describeMarket';
import { describePolyOrderCached, polyOrderLiveData } from '../../lib/polymarket/describeOrder';
import { polyOrderQueueFromBook } from '../../lib/polymarket/orderQueue';
import { americanOdds } from '../../views/ValueDashboard/tradeModalBook';
import {
  estimateAggregateFeeCents,
  estimateOrderFeeCents,
  makerRateForTicker,
} from '../../lib/pricing/fees';
import { applyMakerRebate } from '../../lib/pricing/makerRebate';
import { mirrorCents, roundToCentiCents } from '../../lib/pricing/subCent';
import { titleParts } from './cardFormat';

export interface OpenOrderEconomics {
  /** All-in cost in dollars. Buy: stake + maker fee. Sell: proceeds − fee.
   *  Null when the side-native price isn't known. */
  costDollars: number | null;
  /** Net profit if the contract settles favorably (gross − maker fee).
   *  Null when the price isn't known. */
  toWinDollars: number | null;
  /** Maker-fee estimate in dollars. Null when the series rate isn't loaded /
   *  is `flat` / unknown — caller shows the fee-blind gross rather than a
   *  fabricated 0. Polymarket resolves to a real $0.00 (rate 0), not null. */
  feeDollars: number | null;
  /** Maker-rebate estimate in dollars. Null when the fee schedule is unknown. */
  rebateDollars: number | null;
}

/**
 * Fee-inclusive cost / to-win for a resting open order — the canonical
 * trade-panel economics (see ValueDashboard/GameDetailPanel) carried over to
 * the open-orders surfaces so they stop quoting raw qty × price.
 *
 * A resting limit bills as a MAKER fill, so the fee uses the per-series maker
 * rate through the aggregate per-fill ceil ({@link estimateAggregateFeeCents}),
 * which matches the cent-rounded figure Kalshi's own ticket quotes. Pure: the
 * caller resolves `makerRate` (Kalshi → {@link makerRateForTicker}; Polymarket
 * → 0) so this stays testable and free of the fee-cache singleton.
 *
 *   buy : cost  = stake + net fee
 *   sell: cost  = proceeds − net fee
 *   both: toWin = gross profit − net fee
 */
export function openOrderEconomics(args: {
  economicQty: number;
  bidCents: number | null;
  makerRate: number | null;
  /** MM Program maker-fee rebate fraction (0..1); reduces the maker fee.
   *  0 = no rebate. See lib/pricing/makerRebate. */
  makerRebatePct?: number;
  isSell: boolean;
}): OpenOrderEconomics {
  const { economicQty, bidCents, makerRate, makerRebatePct = 0, isSell } = args;
  if (bidCents == null) {
    return { costDollars: null, toWinDollars: null, feeDollars: null, rebateDollars: null };
  }
  const grossCost = (economicQty * bidCents) / 100;
  const grossWin = (economicQty * mirrorCents(bidCents)) / 100;
  if (makerRate == null) {
    // Rate not loaded / flat / unknown series — show fee-blind gross rather
    // than invent a fee (memory: a null rate renders "—"/raw, never 0).
    return { costDollars: grossCost, toWinDollars: grossWin, feeDollars: null, rebateDollars: null };
  }
  const feeDollars =
    estimateAggregateFeeCents({ priceCents: bidCents, count: economicQty, feeRate: makerRate }) /
    100;
  const rebateDollars =
    makerRebatePct > 0 ? feeDollars * Math.min(makerRebatePct, 1) : 0;
  const netFeeDollars = feeDollars - rebateDollars;
  return {
    costDollars: isSell ? grossCost - netFeeDollars : grossCost + netFeeDollars,
    toWinDollars: grossWin - netFeeDollars,
    feeDollars,
    rebateDollars,
  };
}

/** A quote-style price label: the cents shown and the American odds shown
 *  beside them, both derived from ONE price. Rendered as a unit — see
 *  {@link openOrderQuote}. */
export interface PriceQuote {
  /** The exact cents value the odds were computed from, quantized to the
   *  0.01¢ grain. Null when no price is known. */
  cents: number | null;
  /** Null when there is no price, or at the 0¢/100¢ boundaries where no
   *  American line exists — the renderers then print the cents alone rather
   *  than a dangling `—`. */
  odds: string | null;
}

/**
 * The one semantic price basis behind an open order's `cents / odds` pair.
 *
 * Both halves come out of a single effective price, so the label can never
 * describe a different quantity than the odds beside it (#452: a 53¢ card
 * rendered `53¢ / -117.4`, odds from an unshown fee-loaded 54¢). The basis is:
 *
 *   Raw   → the venue price untouched
 *   Fees  → the per-contract centicent maker fee moved AGAINST the trader:
 *           a buy pays it on top of the bid, a sell nets it out of the ask
 *           (the direction {@link openOrderEconomics} already uses for
 *           proceeds). Adding it on both sides made the Fees toggle read as
 *           if the fee paid the seller.
 *   +MM    → that fee reduced by the program rebate ({@link applyMakerRebate}).
 *
 * A null / flat / zero rate leaves the price raw. Quantizing BEFORE deriving
 * the odds is what makes the printed label literally the odds' input —
 * `fmtCents` then re-rounds a no-op.
 *
 * Deliberately NOT the aggregate (order-total) fee basis: `Cost` / `To Win`
 * bill the whole quantity through Kalshi's per-fill ceil, which at qty 1 turns
 * a 0.44¢ fee into a full cent. That rounding belongs to the order totals, not
 * to a per-contract quote (#452 supersedes #100's shared-rounding attempt).
 */
export function openOrderQuote(args: {
  priceCents: number | null;
  feeAdjusted: boolean;
  makerRate: number | null;
  makerRebatePct?: number;
  /** Resting SELL (ask). Flips the fee's direction; see above. */
  isSell?: boolean;
}): PriceQuote {
  const { priceCents, feeAdjusted, makerRate, makerRebatePct = 0, isSell = false } = args;
  if (priceCents == null) return { cents: null, odds: null };
  // 0¢ / 100¢ carry neither a fee (the quadratic formula is 0 there) nor an
  // American line. Quote the venue price bare rather than the fee helper's
  // 0.01¢ floor stand-in, which rendered a 0¢ last trade as `0.01¢ +999900`.
  if (priceCents <= 0 || priceCents >= 100) {
    return { cents: roundToCentiCents(priceCents), odds: null };
  }
  const { netFeeCents } = applyMakerRebate({
    makerFeeCents: feeAdjusted
      ? estimateOrderFeeCents({ priceCents, count: 1, feeRate: makerRate })
      : 0,
    rebatePct: makerRebatePct,
  });
  const cents = roundToCentiCents(
    isSell ? priceCents - netFeeCents : priceCents + netFeeCents,
  );
  return { cents, odds: americanOdds(cents) };
}

interface BookLevelLite {
  price: number;
  size: number;
}
interface BookLite {
  yes: BookLevelLite[];
  no: BookLevelLite[];
}

export interface OrderCardVM {
  key: string;
  /** Venue-native order id for the cancel call. */
  cancelId: string;
  venue: 'kalshi' | 'polymarket';
  ticker: string;
  title: string;
  subtitle: string;
  eventStartLabel: string | null;
  side: 'yes' | 'no' | null;
  team: string | null;
  /** Partially filled. */
  filling: boolean;
  /** Partially filled while no better level is ahead; drives the amber in-sale UI. */
  activeInSale: boolean;
  isSell: boolean;
  totalQty: number;
  filledQty: number;
  remainingQty: number;
  /** VENUE-RAW side-native order price. This is the order's own price, not a
   *  display basis — the Open Orders surfaces feed it to the edit-price modal
   *  as `currentPriceCents`. Never fold fees into it; the displayed price
   *  lives in {@link bidQuote}. */
  bidCents: number | null;
  /** Displayed bid/ask price + its American odds, one basis (#452). */
  bidQuote: PriceQuote;
  /** Displayed last-trade price + its American odds, one basis (#452). */
  lastQuote: PriceQuote;
  /** 0 = at front of book, N = behind by N¢, null = unknown. */
  bookDiff: number | null;
  /** Absolute position in the FIFO — at-level slot plus everything resting
   *  at strictly better prices. 0 = literal front of book. */
  queue: number | null;
  /** Just the strictly-better levels — total contracts at price(s) above
   *  ours that will fill before our level is even touched. 0 = top of book
   *  (no levels above us). */
  queueAbove: number | null;
  /** Contracts at our exact price level resting behind us in the FIFO
   *  (at-level depth − atLevel). 0 = nobody queued behind us. */
  queueBehind: number | null;
  queueTotal: number | null;
  /** All-in cost incl. maker fee (buy: stake + fee; sell: proceeds − fee). */
  costDollars: number | null;
  /** Net profit if settled favorably (gross − maker fee). */
  toWinDollars: number | null;
  /** Maker-fee estimate in dollars; null when the series rate isn't loaded. */
  feeDollars: number | null;
  restingSinceMs: number | null;
  /** "GTC" or a local expiry time. */
  tifLabel: string;
  /** The order's fixed expiry falls before the event start G1 currently shows,
   *  so it will die pre-game unless the schedule moves back (#517). Whenever
   *  this is true `tifLabel` holds the literal expiry time, never "GTS" — the
   *  two derivations share {@link GTS_EQUIVALENCE_TOLERANCE_MS}, so an expiry
   *  outside the tolerance cannot read as GTS. BOTH venues since #557 — Kalshi
   *  from `expiration_ts`, Polymarket from the CLOB GTD's `expiration`; each
   *  builder documents its own decode, the decision itself is one predicate. */
  expiresBeforeEventStart: boolean;
  /** Polymarket-only replacement hints. Forwarded so edit-price can preserve
   *  fine-tick and NEG_RISK market settings after canceling the old order. */
  tickSize?: string;
  negRisk?: boolean;
  /** False when exact order detail proves cancelability but not a safe
   * amend/decrease path for the old listing. */
  editAvailable?: boolean;
}

/** Side-native price in cents, grain-exact via `mirrorCents`. Kalshi often sends
 *  only yes_price even for a NO order, so a NO price is `mirrorCents(yes)`.
 *  Shared by the order card, the Open Orders table, and the sibling-behind
 *  grouping — a raw `100 - price` here drifts on sub-cent books (breaks the
 *  float-equality at-level match and same-price sibling grouping). */
export function sideNativePriceCents(o: {
  side: 'yes' | 'no';
  yesPrice?: number | null;
  noPrice?: number | null;
}): number | null {
  return o.side === 'yes'
    ? (o.yesPrice ?? (o.noPrice != null ? mirrorCents(o.noPrice) : null))
    : (o.noPrice ?? (o.yesPrice != null ? mirrorCents(o.yesPrice) : null));
}

/** Side-native bid price in cents for a Kalshi order (grain-exact). */
function bidCentsOf(order: KalshiOrder): number | null {
  return sideNativePriceCents(order);
}

/**
 * GTS equivalence tolerance. A Good-to-Start expiry is the scheduled start
 * carried to the venue through an ISO→unix-seconds conversion, so an expiry
 * within a minute of the start IS that start rather than a distinct
 * instruction. Exported so the label, the #517 warning, and the Open Orders
 * table's copy of both cannot drift onto different tolerances.
 */
export const GTS_EQUIVALENCE_TOLERANCE_MS = 60_000;

/** True when a fixed expiry sits close enough to the event start to BE it, and
 *  so reads as "GTS" instead of a literal clock time. */
export function isGtsEquivalentExpiry(
  expirationMs: number | null | undefined,
  eventStartMs: number | null | undefined,
): boolean {
  if (expirationMs == null || eventStartMs == null) return false;
  if (!Number.isFinite(expirationMs) || !Number.isFinite(eventStartMs)) return false;
  return Math.abs(expirationMs - eventStartMs) <= GTS_EQUIVALENCE_TOLERANCE_MS;
}

/**
 * True when an order's FIXED venue expiry falls materially before the event
 * start G1 currently shows (#517).
 *
 * Kalshi captures a Good-to-Start expiry from the scheduled start at PLACEMENT
 * and never moves it again. When an event is postponed the expiry stays put, so
 * an older order can die before the start now on screen while a later order on
 * the same match survives — the production signature traced in #510, where a
 * 10:40 expiry cancelled at exactly 10:40:00 against a moved start.
 *
 * Deliberately DIRECTIONAL: only an expiry EARLIER than the start is a problem.
 * An expiry after it (a schedule that moved earlier, or a custom time past
 * first pitch) outlives the start and has nothing to warn about.
 *
 * A missing timestamp is not a finding. A GTC order has no expiry and an
 * unknown start cannot be compared, so both return false rather than guess.
 * Note the venue gives us no way to tell a GTS expiry from a hand-set custom
 * one — both arrive as a bare `expiration_ts` (docs/kalshi-v2-order-migration
 * .md) — so this is a claim about the timestamp, never about the instruction.
 */
export function expiryPrecedesEventStart(
  expirationMs: number | null | undefined,
  eventStartMs: number | null | undefined,
): boolean {
  if (expirationMs == null || eventStartMs == null) return false;
  if (!Number.isFinite(expirationMs) || !Number.isFinite(eventStartMs)) return false;
  return expirationMs < eventStartMs - GTS_EQUIVALENCE_TOLERANCE_MS;
}

export interface KalshiOrderCtx {
  /** getLastTradeYesCents(ticker) */
  lastTradeYesCents: number | null;
  /** getOrderbookRaw(ticker) */
  book: BookLite | null;
  /** Cumulative queue ahead of this order — at-level slot plus everything
   *  resting at strictly better prices. */
  queue: number | null;
  /** Strictly-better levels only (no at-level slot). */
  queueAbove: number | null;
  /** Contracts at our price level behind us in the FIFO. */
  queueBehind: number | null;
  /** Total resting depth in the book — the queue percentile denominator. */
  queueTotal: number | null;
  /** Scheduled event start for the order's market, local-display formatted by caller. */
  eventStartLabel?: string | null;
  /** Scheduled event start epoch ms, used only to label matching expirations as GTS. */
  eventStartMs?: number | null;
  /** Page-session recent-fills sum for this order — a FALLBACK only, consulted
   *  when the order payload carries neither `fillCount` nor `decreaseCount`
   *  (see kalshiOrderFillStats). The buffer caps at 100 fills, so it undercounts
   *  large orders; venue fields always win when present. */
  filledQty?: number | null;
  /** Whether American odds labels should include maker fees. */
  feeAdjustedOdds?: boolean;
  /** MM Program maker-fee rebate fraction (0..1) folded into the QUOTE pair
   *  ({@link OrderCardVM.bidQuote} / {@link OrderCardVM.lastQuote}) only.
   *  `Cost` / `To Win` stay on the GROSS maker fee — they are order totals, a
   *  separate contract from a per-contract quote (#452), and PR #467's review
   *  asked for those totals preserved. Caller resolves the fraction from the
   *  market's live/pregame state (50% pregame / 100% live); 0 = no rebate,
   *  which is what an org that isn't enrolled or a trader with the MM Rebate
   *  control off always passes. See lib/pricing/makerRebate. */
  quoteMakerRebatePct?: number;
}

/** Filled/total for the qty line (`45,608 / 50,000`) — Gitea #91.
 *
 *  Every branch keeps the identity `totalQty = remaining + filled`, which equals
 *  Kalshi's initial_count minus queue-preserving decreases whenever `filled` is
 *  exact — so the denominator holds still while fills arrive and moves only when
 *  the user explicitly decreases the order. The old bug was feeding this identity
 *  a window-capped fills count: on a 50k order the line collapsed to the
 *  page-session view (`6,297/10,053`). Preference order for `filled`:
 *    1. `fillCount` — venue lifetime matched count, exact;
 *    2. `count − remaining − decreaseCount` — exact when decreases are reported;
 *    3. `recentFilledQty` — page-session fills; never mistakes a decrease for a
 *       fill, but undercounts past the 100-fill buffer;
 *    4. `count − remaining` — overstates after a decrease; last resort.
 */
export function kalshiOrderFillStats(
  order: KalshiOrder,
  recentFilledQty?: number | null,
): { filledQty: number; totalQty: number; remainingQty: number; filling: boolean } {
  const remainingQty = order.remainingCount;
  const filledQty =
    order.fillCount != null
      ? Math.max(0, order.fillCount)
      : order.decreaseCount != null
        ? Math.max(0, order.count - remainingQty - order.decreaseCount)
        : recentFilledQty != null
          ? Math.max(0, Math.round(recentFilledQty))
          : Math.max(0, order.count - remainingQty);
  return { filledQty, totalQty: remainingQty + filledQty, remainingQty, filling: filledQty > 0 };
}

export function buildKalshiOrderVM(
  order: KalshiOrder,
  ctx: KalshiOrderCtx,
): OrderCardVM {
  const { title, subtitle, team } = titleParts(order.ticker, order.side);
  const bidCents = bidCentsOf(order);
  const isSell = order.action === 'sell';

  // Last trade — hydra YES-cents, inverted for a NO order (exact mirror).
  const lastCents =
    ctx.lastTradeYesCents == null
      ? null
      : order.side === 'yes'
        ? ctx.lastTradeYesCents
        : mirrorCents(ctx.lastTradeYesCents);

  // Book reference: a BUY compares to same-side bids; a SELL to the implied
  // ask (100 − opposite-side bid). bookDiff 0 = at front, N = behind by N¢.
  let topRef: number | null = null;
  if (ctx.book) {
    const refLevels = isSell
      ? order.side === 'yes'
        ? ctx.book.no
        : ctx.book.yes
      : order.side === 'yes'
        ? ctx.book.yes
        : ctx.book.no;
    if (refLevels.length > 0) {
      let max = -Infinity;
      for (const lvl of refLevels) if (lvl.price > max) max = lvl.price;
      if (Number.isFinite(max)) topRef = isSell ? mirrorCents(max) : max;
    }
  }
  // Quantize to the 0.01¢ grain: on a deci-cent market this is a subtraction
  // of two sub-cent floats, and IEEE754 renders it raw — 58.5 − 54.3 became
  // "Behind 4.200000000000003¢" on a live card. Same centi-cent invariant the
  // rest of this module's price math follows (see lib/pricing/subCent).
  const bookDiff =
    topRef != null && bidCents != null
      ? Math.max(0, roundToCentiCents(isSell ? bidCents - topRef : topRef - bidCents))
      : null;

  const { filledQty, totalQty, remainingQty } = kalshiOrderFillStats(order, ctx.filledQty);
  // Economics on the filled portion once anything has filled, else on the
  // whole order — matches the trader mockup's cost/to-win figures.
  const economicQty = filledQty > 0 ? filledQty : totalQty;
  // Resting limit → maker fill; fold the per-series maker fee into the
  // displayed economics so the card matches the trade panel / Kalshi ticket.
  // GROSS fee on purpose — no `makerRebatePct` here. The MM rebate reaches the
  // quote pair below and stops there; these totals keep the fee Kalshi's own
  // ticket quotes (see KalshiOrderCtx.quoteMakerRebatePct).
  const econ = openOrderEconomics({
    economicQty,
    bidCents,
    makerRate: makerRateForTicker(order.ticker),
    isSell,
  });
  // Quote pairs — a resting limit is a maker fill either way, so both the
  // bid/ask and the last-trade label carry the maker rate, and both take the
  // order's own fee direction (a sell card quotes what it would NET).
  // `lastCents` stays unrounded going in: a 0.1¢ last trade must not quantize
  // to 0 (nulls odds).
  const quoteArgs = {
    feeAdjusted: ctx.feeAdjustedOdds ?? false,
    makerRate: makerRateForTicker(order.ticker),
    makerRebatePct: ctx.quoteMakerRebatePct ?? 0,
    isSell,
  };
  const bidQuote = openOrderQuote({ priceCents: bidCents, ...quoteArgs });
  const lastQuote = openOrderQuote({ priceCents: lastCents, ...quoteArgs });

  let tifLabel = 'GTC';
  let expiresBeforeEventStart = false;
  if (order.expirationTs != null) {
    const expMs = order.expirationTs * 1000;
    tifLabel = isGtsEquivalentExpiry(expMs, ctx.eventStartMs)
      ? 'GTS'
      : new Date(expMs).toLocaleTimeString([], {
          hour: 'numeric',
          minute: '2-digit',
        });
    expiresBeforeEventStart = expiryPrecedesEventStart(expMs, ctx.eventStartMs);
  }

  return {
    key: order.orderId,
    cancelId: order.orderId,
    venue: 'kalshi',
    ticker: order.ticker,
    title,
    subtitle,
    eventStartLabel: ctx.eventStartLabel ?? null,
    side: order.side,
    team,
    filling: filledQty > 0,
    activeInSale: filledQty > 0 && ctx.queueAbove === 0,
    isSell,
    totalQty,
    filledQty,
    remainingQty,
    bidCents,
    bidQuote,
    lastQuote,
    bookDiff,
    queue: ctx.queue,
    queueAbove: ctx.queueAbove,
    queueBehind: ctx.queueBehind,
    queueTotal: ctx.queueTotal,
    expiresBeforeEventStart,
    costDollars: econ.costDollars,
    toWinDollars: econ.toWinDollars,
    feeDollars: econ.feeDollars,
    restingSinceMs: order.createdTime ? Date.parse(order.createdTime) : null,
    tifLabel,
    editAvailable: canEditKalshiOrder(order),
  };
}

export function buildPolyOrderVM(order: PolyRelayOpenOrder): OrderCardVM {
  const id = String(order.id ?? order.orderID ?? order.orderId ?? '');
  const assetId = String(order.asset_id ?? order.market ?? id);
  const isSell = String(order.side ?? '').toUpperCase() === 'SELL';
  const priceFrac =
    typeof order.price === 'number' ? order.price : Number(order.price ?? NaN);
  // Centi-cent grain — whole-cent rounding zeroed sub-cent CLOB order prices.
  const bidCents = Number.isFinite(priceFrac) ? Math.round(priceFrac * 10000) / 100 : null;
  const orig =
    typeof order.original_size === 'number'
      ? order.original_size
      : Number(order.original_size ?? 0);
  const matched =
    typeof order.size_matched === 'number'
      ? order.size_matched
      : Number(order.size_matched ?? 0);
  const remainingQty = Math.max(0, orig - matched);
  const filledQty = Math.max(0, matched);
  const economicQty = filledQty > 0 ? filledQty : orig;
  // Polymarket has no maker fee in our model → rate 0 (a real $0.00, not an
  // unknown). Keeps the same fee-inclusive shape as the Kalshi path.
  const econ = openOrderEconomics({
    economicQty,
    bidCents,
    makerRate: 0,
    isSell,
  });

  // Resolve the asset_id (tokenId) to a human market description via the
  // cached marketStore lookup (this builder runs per order per page tick —
  // the label path; the replace flow re-derives tickSize/negRisk FRESH at
  // click time). Falls back to the truncated hex tokenId until the sport
  // loads; unresolved ids retry on the cache's wall-clock cadence.
  const desc = describePolyOrderCached(assetId);
  const title = desc
    ? `${desc.matchup} — ${desc.sport.toUpperCase()} — ${desc.date.slice(5)}`
    : truncateTicker(assetId);
  const subtitle = desc
    ? `(${desc.betTeam}) ${desc.side.toUpperCase()}`
    : truncateTicker(assetId);

  // LIVE market data (last/volume/book/start) read fresh by slug ticker —
  // the cached desc only carries immutable labels + pmTicker. Absent until
  // the sport tab resolves the market.
  const live = desc ? polyOrderLiveData(assetId, desc.pmTicker) : null;
  const lastCents = live?.lastCents ?? null;

  // Queue metrics from the order token's own bid ladder. BUY only: a resting
  // SELL's queue is on the opposite token's book (close-path only) and left
  // null rather than risk a wrong number. queueBehind is genuinely unknowable
  // on Polymarket (no per-order FIFO slot) → always null; `queue` is a
  // pessimistic upper bound. See lib/polymarket/orderQueue.
  const q =
    !isSell && bidCents != null && live
      ? polyOrderQueueFromBook(live.sideBids, bidCents)
      : null;

  // Resting age: OpenOrder.created_at is unix SECONDS (present via the relay
  // passthrough index signature, not on the declared type).
  const createdSec = Number((order as Record<string, unknown>).created_at);
  const restingSinceMs =
    Number.isFinite(createdSec) && createdSec > 0 ? createdSec * 1000 : null;

  return {
    key: `poly-${id}`,
    cancelId: id,
    venue: 'polymarket',
    ticker: assetId,
    title,
    subtitle,
    eventStartLabel: formatPolyStartLocal(live?.eventStartMs ?? null),
    side: desc?.side ?? null,
    team: desc?.betTeam ?? null,
    filling: filledQty > 0,
    // Amber "In sale" pill asserts "filling AND no better level ahead" —
    // gate it on the now-computed queueAbove so it can't contradict the
    // level-above stat. queueAbove null (sell / no book) → don't claim
    // front-of-book (still shows `filling`, just not the stronger pill).
    activeInSale: filledQty > 0 && q?.queueAbove === 0,
    isSell,
    totalQty: orig,
    filledQty,
    remainingQty,
    bidCents,
    // Polymarket carries no maker fee in our model, so Raw and Fees are the
    // same basis — the quote pair is the raw price either way (and the fee
    // direction `isSell` would flip is a no-op at rate 0).
    bidQuote: openOrderQuote({ priceCents: bidCents, feeAdjusted: false, makerRate: 0, isSell }),
    lastQuote: openOrderQuote({ priceCents: lastCents, feeAdjusted: false, makerRate: 0, isSell }),
    bookDiff: q?.bookDiff ?? null,
    queue: q?.queue ?? null,
    queueAbove: q?.queueAbove ?? null,
    queueBehind: null,
    queueTotal: q?.queueTotal ?? null,
    costDollars: econ.costDollars,
    toWinDollars: econ.toWinDollars,
    feeDollars: econ.feeDollars,
    restingSinceMs,
    tifLabel: polyTifLabel(order, live?.eventStartMs ?? null),
    // Same predicate as Kalshi, deliberately with no venue branch (#557).
    // Polymarket's GTS is a CLOB GTD whose `expiration` is the event start
    // captured at PLACEMENT: the order references no event or schedule, the
    // CLOB has no modify/amend endpoint, and the readback returns the
    // timestamp we submitted — so a postponement strands it exactly as it
    // strands a Kalshi one. #557 records the venue evidence.
    //
    // The venue also kills a GTD ~60s BEFORE its stated expiry (a security
    // threshold, 10s in 2023 → 60s today). That never reaches this
    // comparison: the threshold is evaluated against the stored timestamp
    // rather than rewriting it, so an unchanged schedule still reads back
    // expiry == start, inside GTS_EQUIVALENCE_TOLERANCE_MS. A tunable venue
    // margin has no business in shared arithmetic.
    expiresBeforeEventStart: expiryPrecedesEventStart(
      polyExpirationMs(order),
      live?.eventStartMs ?? null,
    ),
    tickSize: desc?.tickSize,
    negRisk: desc?.negRisk,
    editAvailable: true,
  };
}

// Browser-local, NO timeZone option — matches marketStore/tracker
// formatStartTimeLocal and the Kalshi order card's own labels so both
// venues read the same clock on one screen (a hardcoded PT zone showed a
// non-PT viewer poly starts 3h off from the adjacent Kalshi card).
const _POLY_TIME_FMT = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});

/** Browser-local h:mm label for a poly event start ms, matching the
 *  tracker's formatStartTimeLocal shape ("3:35p"). Null when unknown. */
function formatPolyStartLocal(ms: number | null): string | null {
  if (ms == null || !Number.isFinite(ms)) return null;
  const parts = _POLY_TIME_FMT.formatToParts(new Date(ms));
  const hour = parts.find((p) => p.type === 'hour')?.value ?? '';
  const minute = parts.find((p) => p.type === 'minute')?.value ?? '';
  const ampm = (parts.find((p) => p.type === 'dayPeriod')?.value ?? '').toLowerCase().startsWith('p')
    ? 'p'
    : 'a';
  return hour && minute ? `${hour}:${minute}${ampm}` : null;
}

/**
 * A Polymarket open order's fixed venue expiry, epoch ms — or null for GTC.
 *
 * The ONE place that knows the CLOB's readback encoding, so the label, the
 * #517 warning and the Open Orders table all read the same value from the same
 * parse. `expiration` arrives through the relay's verbatim row passthrough
 * (`PolyRelayOpenOrder`'s index signature), as unix SECONDS in a string.
 *
 * GTC arrives as `'0'`, as `null`, or absent, depending on venue vintage
 * (#557) — `Number` maps the first two to 0 and the last to NaN, and both
 * fall out here as "no expiry", never as an epoch-0 expiry.
 */
export function polyExpirationMs(order: PolyRelayOpenOrder): number | null {
  const expSec = Number((order as Record<string, unknown>).expiration);
  if (!Number.isFinite(expSec) || expSec <= 0) return null;
  return expSec * 1000;
}

/** Time-in-force label from the CLOB order — BARE (the card prepends
 *  "exp "). A GTD order whose expiration lands on the event start reads as
 *  GTS (good-till-start), matching the ticket's own labeling and the Kalshi
 *  tifLabel shape — through the SHARED {@link isGtsEquivalentExpiry}, so the
 *  two venues cannot drift onto different tolerances (#557; this carried its
 *  own `< 60_000` copy, one boundary tighter than the shared `<=`).
 *  Exported for the orders TABLE's poly rows (card↔table parity). */
export function polyTifLabel(order: PolyRelayOpenOrder, eventStartMs: number | null): string {
  const expMs = polyExpirationMs(order);
  if (expMs == null) return 'GTC';
  if (isGtsEquivalentExpiry(expMs, eventStartMs)) return 'GTS';
  return formatPolyStartLocal(expMs) ?? 'GTD';
}

// Queue depth tiers — absolute thresholds (per trader spec):
//   queue 0 = front of book (pulse), <50k = green, 50–300k = yellow,
//   ≥300k = red. `total` is no longer needed for tiering but is kept on
//   the tone helpers' signatures so existing call sites don't have to
//   change.
type QueueTier = 'unknown' | 'front' | 'green' | 'yellow' | 'red';

function queueTier(queue: number | null): QueueTier {
  if (queue == null) return 'unknown';
  if (queue === 0) return 'front';
  if (queue < 50_000) return 'green';
  if (queue < 300_000) return 'yellow';
  return 'red';
}

/** Queue depth -> value tone. Used in both card and table views so the
 *  queue number reads identically; only the layout surface changes. */
export function queueTone(
  queue: number | null,
  _total: number | null,
): string {
  switch (queueTier(queue)) {
    case 'unknown':
      return 'text-muted-foreground';
    case 'front':
    case 'green':
      return 'text-green-400';
    case 'yellow':
      return 'text-yellow-400';
    case 'red':
      return 'text-red-400';
  }
}

/** In-line-behind depth -> value tone. Unlike queue/level-above, larger is
 *  better here because more contracts are resting behind the user's order. */
export function queueBehindTone(queueBehind: number | null): string {
  if (queueBehind == null) return 'text-muted-foreground';
  if (queueBehind <= 30_000) return 'text-red-400';
  if (queueBehind < 150_000) return 'text-yellow-400';
  return 'text-green-400';
}

/** Contracts resting BEHIND the user's displayed order at its price level.
 *  `atLevelDepth` is the book's total size at the level — which INCLUDES the
 *  user's own same-price orders; `atLevelAhead` is the depth ahead of the
 *  displayed order; `ownRemainingQty` is the displayed order's own size; and
 *  `ownSiblingBehindQty` is the summed remaining size of the user's OTHER
 *  same-price orders that are KNOWN to sit behind this one (via their queue
 *  position). Subtracting it stops an own sibling from being miscounted as
 *  market depth behind you. Defaults to 0 → a single order is unchanged. The
 *  `Math.max` floor means stale/racy depth can never yield a negative. */
export function queueBehindAtPriceLevel(
  atLevelDepth: number,
  atLevelAhead: number,
  ownRemainingQty: number,
  ownSiblingBehindQty = 0,
): number {
  return Math.max(
    0,
    atLevelDepth - atLevelAhead - ownRemainingQty - ownSiblingBehindQty,
  );
}

/** A resting order reduced to what the "in line behind" sibling math needs:
 *  identity, its price level, its queue position, and remaining size. #84. */
export interface SiblingOrder {
  orderId: string;
  ticker: string;
  side: 'yes' | 'no';
  action: 'buy' | 'sell';
  /** Side-native price in cents, or null if unknown. */
  priceCents: number | null;
  /** Contracts ahead of this order at its level (Kalshi FIFO slot), or null. */
  queuePosition: number | null;
  remainingCount: number;
}

/** Grouping key that collapses orders sharing a price level: ticker, side,
 *  action, and grain-exact price. Null when the price is unknown (ungroupable).
 *  Callers group once by this key so the sibling scan is O(group), not O(all
 *  orders) on the 4Hz metrics rebuild. */
export function siblingGroupKey(o: SiblingOrder): string | null {
  return o.priceCents == null
    ? null
    : `${o.ticker}|${o.side}|${o.action}|${o.priceCents}`;
}

/** Summed remaining size of `self`'s OWN siblings that are KNOWN to sit behind
 *  it. `group` must be the orders sharing self's {@link siblingGroupKey} (same
 *  ticker, side, action, grain-exact price) — the caller groups once so this
 *  stays O(group), not O(all orders). A member counts only when its queue
 *  position places it past self's whole block (queuePosition >=
 *  self.queuePosition + self.remainingCount); members with an unknown position
 *  are excluded — never subtract on a guess (#84). Returns 0 when self has no
 *  known position. */
export function ownSiblingBehindQty(
  self: SiblingOrder,
  group: ReadonlyArray<SiblingOrder>,
): number {
  if (self.queuePosition == null) return 0;
  const behindThreshold = self.queuePosition + self.remainingCount;
  let sum = 0;
  for (const c of group) {
    if (c.orderId === self.orderId || c.queuePosition == null) continue;
    if (c.queuePosition >= behindThreshold) sum += c.remainingCount;
  }
  return sum;
}

/** Queue depth -> table row tint. */
export function queueRowTone(
  queue: number | null,
  _total: number | null,
): string {
  switch (queueTier(queue)) {
    case 'front':
      return 'animate-queue-front-pulse';
    case 'green':
      return 'bg-green-500/20';
    case 'yellow':
      return 'bg-yellow-500/20';
    case 'red':
      return 'bg-red-500/20';
    default:
      return '';
  }
}

/** Queue depth -> card container rail (left accent strip). */
export function queueCardTone(
  queue: number | null,
  _total: number | null,
): string {
  switch (queueTier(queue)) {
    case 'front':
    case 'green':
      return 'bg-green-400';
    case 'yellow':
      return 'bg-yellow-400';
    case 'red':
      return 'bg-red-400';
    default:
      return '';
  }
}

/** Queue-front value pulse. Applied to the value itself; table/card
 *  container treatments remain separate. */
export function queueValuePulse(queue: number | null): string {
  return queue === 0 ? 'animate-queue-front-text-pulse' : '';
}
